/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, IconButton, InputLabel, LinearProgress, Menu, MenuItem, Paper, Select, Snackbar, SnackbarCloseReason, Switch, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, TextField, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { Environment } from '../../../shared/environment';
import dayjs, { Dayjs } from 'dayjs';
import { FerramentasDaListagemFinanceiroConciliacao, LinhasEsqueletoTabela, TelaBloqueioSalvando } from '../../../shared/components';
import { EmptyState, FilterBar } from '../../../shared/components/ui';
import { useBloqueioSalvamento } from '../../../shared/hooks';
import { CaixaConstasService, IListagem, IPorFormaPagamento, IRegistroPorOperacao, IResultado, ISaldoPorConta, ITotaisRecebidos, ITotalPorTipo, IVoltarLancamento } from '../../../shared/services/api/Financeiro/Caixa';
import { CaixaTurnoAdminService, TCaixaTurnoStatus } from '../../../shared/services/api/Financeiro/CaixaTurnoAdmin';
import { ordenarCategoriasPagamento } from '../../../shared/utils/formasPagamentoCaixa';
import { formatarMoeda } from '../../../shared/utils/formatarMoeda';
import { TTipoContaFinanceira } from '../../../shared/services/api/cadastros/ContaBancaria';
import { LayoutBaseDeFinanceiroConciliacao } from '../../../shared/layouts';
import { SidebarContasConciliacao } from './SidebarContasConciliacao';
import ReplyIcon from '@mui/icons-material/Reply';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import PDFCaixa from './Impressaocaixa';
import { DialoFecharCaixa } from './DialogFecharCaixa';
import { DialogAbrirCaixa } from './DialogAbrirCaixa';
import { DialogTransferirRecebivel } from './DialogTransferirRecebivel';
import { DialogTransferenciaConta, TDestinoTransferenciaConta } from './DialogTransferenciaConta';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaidIcon from '@mui/icons-material/Paid';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import PixIcon from '@mui/icons-material/Pix';
import CreditScoreIcon from '@mui/icons-material/CreditScore';

type TCategoriaLinha = 'creditos' | 'debitos' | 'transferencias' | 'sangrias';
type TAbaFiltro = TCategoriaLinha | 'todos' | 'suprimentos';
type TPeriodoSaldosPreset = 'hoje' | 'ontem' | '7dias' | 'mes' | 'personalizado';

const ABAS_FILTRO: { valor: TAbaFiltro; label: string }[] = [
  { valor: 'todos', label: 'Todos' },
  { valor: 'creditos', label: 'Créditos' },
  { valor: 'debitos', label: 'Débitos' },
  { valor: 'transferencias', label: 'Transferências' },
  { valor: 'sangrias', label: 'Sangrias' },
  { valor: 'suprimentos', label: 'Suprimentos' },
];

// Mesma classificação usada pelas abas de filtro e pelo badge da coluna Tipo
// — assim o ícone de cada linha sempre bate com a aba onde ela aparece.
// Sangria é uma baixa de contas a pagar como outra qualquer (tipo 2),
// só reconhecível pelo texto do histórico (ver Sangria.ts, backend).
// Abertura/fechamento de turno e transferência entre caixas compartilham o
// mesmo tipo (5/6, ver GetTurnoStatus.ts/TransferenciaCaixa.ts, backend).
// Mesmo texto usado pelo backend (CAIXA_TURNO_ABERTURA_HISTORICO/
// CAIXA_TURNO_FECHAMENTO_HISTORICO, ver GetTurnoStatus.ts) — abertura e
// fechamento de turno não podem ser desfeitos por "Voltar lançamento" (o
// backend recusa), então nem oferece a ação aqui, pra não abrir um diálogo
// de confirmação mostrando valores zerados sem eu poder de fato reverter.
const ehTurnoAberturaOuFechamento = (historico: string) =>
  historico?.startsWith('Abertura de caixa (PDV)') || historico?.startsWith('Fechamento de caixa (PDV)');

const classificarLinha = (row: IListagem): TCategoriaLinha => {
  if (row.tipos === 5 || row.tipos === 6) return 'transferencias';
  if (row.tipos === 2 && row.historico?.startsWith('Sangria')) return 'sangrias';
  return row.entrada > 0 ? 'creditos' : 'debitos';
};

const BADGE_POR_CATEGORIA: Record<TCategoriaLinha, { icon: React.ReactElement; cor: string }> = {
  creditos: { icon: <ArrowUpwardIcon fontSize="small" />, cor: '#2e7d32' },
  debitos: { icon: <ArrowDownwardIcon fontSize="small" />, cor: '#c62828' },
  transferencias: { icon: <SwapHorizIcon fontSize="small" />, cor: '#6a1b9a' },
  sangrias: { icon: <RemoveCircleOutlineIcon fontSize="small" />, cor: '#e65100' },
};

// `datalan` é só a data (sem hora, ver GetAll.ts no backend) — a hora vem de
// `criadoem`. Registros muito antigos, gravados antes dessa coluna existir
// direito, podem cair em 00:00:00; nesses casos mostra só a data, pra não
// sugerir uma hora que não existe.
const formatarDataHora = (datalan: string | Date, criadoem?: string | Date) => {
  const data = dayjs(datalan).format('DD/MM/YYYY');
  if (!criadoem) return data;
  const hora = dayjs(criadoem);
  return hora.format('HH:mm:ss') === '00:00:00' ? data : `${data} ${hora.format('HH:mm')}`;
};

const formatarDataHoraRevertido = (revertidoem?: string | Date | null) => {
  if (!revertidoem) return '';
  return `Revertido em ${dayjs(revertidoem).format('DD/MM/YYYY [às] HH:mm')}`;
};

export const Conciliacao: React.FC = () => {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<IListagem[]>([]);
  const [rowsVoltar, setRowsVoltar] = useState<IVoltarLancamento | undefined>(undefined);
  const [registrosParaVoltar, setRegistrosParaVoltar] = useState<IRegistroPorOperacao[]>([]);
  const [carregandoRegistrosVoltar, setCarregandoRegistrosVoltar] = useState(false);
  // Guarda a última operação pedida — se o usuário clicar em "Voltar" de um
  // lançamento, cancelar e clicar em outro rapidamente, a resposta da
  // primeira busca pode chegar DEPOIS da segunda e sobrescrever com dados de
  // outra operação. Só aplica a resposta se ela for realmente da última
  // pedida.
  const idoperacaoEmAberto = useRef<string | null>(null);
  const { isSaving: isVoltando, progress: voltarProgress, iniciar: iniciarVoltar, concluir: concluirVoltar, cancelar: cancelarVoltar } = useBloqueioSalvamento();
  const [resultados, setResultados] = useState<IResultado | undefined>(undefined);
  const [totaisPorTipo, setTotaisPorTipo] = useState<ITotalPorTipo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openFechar, setOpenFechar] = useState(false);
  const [openAbrir, setOpenAbrir] = useState(false);
  const [openTransferirRecebivel, setOpenTransferirRecebivel] = useState(false);
  const [openTransferenciaConta, setOpenTransferenciaConta] = useState(false);
  const [destinoTransferenciaConta, setDestinoTransferenciaConta] = useState<TDestinoTransferenciaConta>('banco');
  const [dataInicial, setDataInicial] = useState(dayjs().format('DD/MM/YYYY')); 
  const [dataFinal, setDataFinal] = useState(dayjs().format('DD/MM/YYYY'));  
  const [status, setStatus] = useState(false); 
  const [conta, setConta] = useState('');
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error'  | 'info'  | 'success'>('warning');  
        
  const [openConfirm, setOpenConfirm] = useState(false);
  
  // Estado para controlar se a conta já foi selecionada e pesquisada
  const [contaSelecionada, setContaSelecionada] = useState('');
  const [descricaoContaSelecionada, setDescricaoContaSelecionada] = useState('');
  // Contas 'recebiveis' não têm turno/extrato de caixa — a área principal
  // vira um placeholder em vez do fluxo normal (ver render mais abaixo).
  const [tipoContaSelecionada, setTipoContaSelecionada] = useState<TTipoContaFinanceira>();
  // Sem os botões de Abrir/Fechar Sessão (removidos da Conciliação — só quem
  // está com o caixa aberto pode mexer nele), esse chip vira a única pista de
  // que uma transferência pra esse caixa vai ser recusada por falta de
  // sessão aberta.
  const [statusSessaoCaixa, setStatusSessaoCaixa] = useState<TCaixaTurnoStatus | null>(null);

  // Estado para controlar se já foi realizada uma pesquisa
  const [pesquisaRealizada, setPesquisaRealizada] = useState(false);

  // Filtro de categoria (Todos/Créditos/Débitos/...) — só recorta os `rows`
  // já buscados, sem chamada nova à API. Sem paginação: a listagem inteira
  // fica visível rolando dentro do container (ver TableContainer abaixo).
  const [abaFiltro, setAbaFiltro] = useState<TAbaFiltro>('todos');

  // Menu de ações ("⋮") por linha — guarda qual linha está com o menu aberto.
  const [menuLinha, setMenuLinha] = useState<{ idcaixa: string; anchorEl: HTMLElement } | null>(null);

  const [openDetalhamento, setOpenDetalhamento] = useState(false);
  // Dialog "Detalhar Saldos" (botão da toolbar) — saldo de cada conta
  // individual (recebíveis, contas bancárias, caixas e cofre), diferente do
  // openDetalhamento acima (que é o saldo real por forma de pagamento da
  // conta selecionada). Busca sob demanda, só quando o dialog abre.
  const [openSaldosPorConta, setOpenSaldosPorConta] = useState(false);
  const [carregandoSaldosPorConta, setCarregandoSaldosPorConta] = useState(false);
  const [saldosPorConta, setSaldosPorConta] = useState<ISaldoPorConta[]>([]);
  const [recebiveisPorFormaPagamento, setRecebiveisPorFormaPagamento] = useState<IPorFormaPagamento[]>([]);
  // Totais BRUTOS recebidos no período (só entrada, sem descontar saída) —
  // diferente dos grupos "saldosAgrupados" abaixo, que são saldo líquido.
  const [totaisRecebidos, setTotaisRecebidos] = useState<ITotaisRecebidos>({
    credito: 0, debito: 0, pixDinamico: 0, dinheiro: 0, contasBancarias: 0,
  });
  const [periodoSaldosPreset, setPeriodoSaldosPreset] = useState<TPeriodoSaldosPreset>('hoje');
  const [dataInicialSaldos, setDataInicialSaldos] = useState<Dayjs | null>(dayjs());
  const [dataFinalSaldos, setDataFinalSaldos] = useState<Dayjs | null>(dayjs());
  // Grupo com o chevron fechado (por padrão todos ficam abertos, igual a imagem).
  const [gruposSaldosFechados, setGruposSaldosFechados] = useState<Set<string>>(new Set());
  // Linha cujo histórico está sendo exibido por completo no diálogo de
  // detalhe — a coluna Histórico da tabela é truncada (ver célula abaixo)
  // pra não crescer com textos longos (ex.: lançamento revertido, que ganha
  // o sufixo "[Revertido - Sessão ...]").
  const [linhaDetalhe, setLinhaDetalhe] = useState<IListagem | null>(null);
  // Vem pronto do backend (resultados.porFormaPagamento), restrito ao mesmo
  // período pesquisado em [datainicial, datafinal] — só o que está nas
  // linhas exibidas na tela, sem puxar lançamentos de outros dias.
  const detalhamentoPorFormaPagamento = useMemo(
    () => new Map((resultados?.porFormaPagamento || []).map((f) => [f.categoria, f.valor])),
    [resultados],
  );
  const categoriasDetalhamento = useMemo(
    () => ordenarCategoriasPagamento(Array.from(detalhamentoPorFormaPagamento.keys())),
    [detalhamentoPorFormaPagamento],
  );

  // Totais gerais (recebíveis/cofre/caixa/contas bancárias), exibidos nos
  // cards antes da primeira pesquisa de conta (ver LayoutBaseDeFinanceiroConciliacao).
  useEffect(() => {
    CaixaConstasService.getTotaisPorTipo().then((result) => {
      if (!(result instanceof Error)) setTotaisPorTipo(result);
    });
  }, []);

  const totalPorTipo = (tipo: ITotalPorTipo['tipo']) =>
    formatarMoeda(totaisPorTipo.find((t) => t.tipo === tipo)?.saldo ?? 0);

  // Mesmos 4 grupos e rótulos dos StatCards (LayoutBaseDeFinanceiroConciliacao),
  // só que aqui é o drill-down: Recebíveis por forma de pagamento, os demais
  // por conta individual.
  const GRUPOS_SALDOS_POR_CONTA: { tipo: ISaldoPorConta['tipo']; label: string; descricao: string; icon: React.ReactElement }[] = [
    { tipo: 'recebiveis', label: 'Recebíveis', descricao: 'Valores a receber por forma de pagamento', icon: <CreditCardIcon /> },
    { tipo: 'banco', label: 'Contas Bancárias', descricao: 'Saldos das contas bancárias', icon: <AccountBalanceIcon /> },
    { tipo: 'caixa', label: 'Caixas', descricao: 'Saldos dos caixas', icon: <AccountBalanceWalletIcon /> },
    { tipo: 'cofre', label: 'Cofre', descricao: 'Valores armazenados no cofre', icon: <LockOutlinedIcon /> },
  ];
  const saldosAgrupados = useMemo(
    () => GRUPOS_SALDOS_POR_CONTA.map((grupo) => {
      const itens = grupo.tipo === 'recebiveis'
        ? recebiveisPorFormaPagamento.map((f) => ({ chave: f.categoria, label: f.categoria, valor: f.valor }))
        : saldosPorConta
          .filter((c) => c.tipo === grupo.tipo)
          .map((c) => ({ chave: c.idconta, label: c.numeroconta ? `${c.titular} - ${c.numeroconta}` : c.titular, valor: c.saldo }));
      return {
        ...grupo,
        itens,
        total: itens.reduce((soma, item) => soma + item.valor, 0),
      };
    }),
    [saldosPorConta, recebiveisPorFormaPagamento],
  );
  const saldoTotalGeral = useMemo(
    () => saldosAgrupados.reduce((soma, grupo) => soma + grupo.total, 0),
    [saldosAgrupados],
  );

  // Cards de "Totais Recebidos" (valor bruto, só entrada) — Crédito/Débito/
  // PIX Dinâmico vêm da conta de recebíveis correspondente (única rota
  // dessas formas); Dinheiro é a soma de entrada em todos os caixas; Contas
  // Bancárias é a soma de entrada em todas as contas banco (PIX Estático,
  // transferência de recebível, entre bancos ou depósito do cofre — ver
  // SaldosPorConta.ts, backend).
  const itensTotaisRecebidos: { label: string; valor: number; icon: React.ReactElement; cor: string }[] = [
    { label: 'Crédito', valor: totaisRecebidos.credito, icon: <CreditCardIcon fontSize="small" />, cor: '#2e7d32' },
    { label: 'Débito', valor: totaisRecebidos.debito, icon: <CreditScoreIcon fontSize="small" />, cor: '#1565c0' },
    { label: 'PIX Dinâmico', valor: totaisRecebidos.pixDinamico, icon: <PixIcon fontSize="small" />, cor: '#00897b' },
    { label: 'Dinheiro', valor: totaisRecebidos.dinheiro, icon: <PaidIcon fontSize="small" />, cor: '#ef6c00' },
    { label: 'Contas Bancárias', valor: totaisRecebidos.contasBancarias, icon: <AccountBalanceIcon fontSize="small" />, cor: '#6a1b9a' },
  ];


  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event, 
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
    if(tms === 'success'){ 
      // navigate('/contabancaria');
    }
  };
        
  const handleVoltar = (idoperacao: string, datacaixa: Date, idconta: string, tipo: number) => {
    const voltarlancamento = {
      idoperacao: idoperacao,
      datacaixa: datacaixa,
      idconta: idconta,
      tipos: tipo,
    };
    setRowsVoltar(voltarlancamento);
    // Um idoperacao pode agrupar lançamentos em MAIS de uma conta (ex: venda
    // paga parte em Dinheiro no caixa do PDV, parte em Cartão na conta de
    // recebíveis) — não dá pra montar essa lista só com os `rows` já
    // carregados (que são só da conta pesquisada na tela), por isso busca no
    // backend. Ele já filtra fora o que já foi revertido antes.
    setRegistrosParaVoltar([]);
    setCarregandoRegistrosVoltar(true);
    setOpenConfirm(true);
    idoperacaoEmAberto.current = idoperacao;
    CaixaConstasService.getPorOperacao(idoperacao).then((result) => {
      // Resposta de um clique anterior chegando atrasada — ignora, quem
      // manda é a busca da operação atualmente aberta no diálogo.
      if (idoperacaoEmAberto.current !== idoperacao) return;
      setCarregandoRegistrosVoltar(false);
      if (result instanceof Error) {
        setOpenConfirm(false);
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
        return;
      }
      setRegistrosParaVoltar(result);
    });
  };

  const totalEntradaVoltar = registrosParaVoltar.reduce((acc, r) => acc + Number(r.entrada || 0), 0);
  const totalSaidaVoltar = registrosParaVoltar.reduce((acc, r) => acc + Number(r.saida || 0), 0);

  const handleConfirmDelete = () => {
    if (typeof rowsVoltar !== 'object') return;
    if (!iniciarVoltar()) return;
    CaixaConstasService
      .VoltarLancamento(rowsVoltar)
      .then((result) => {
        if (result instanceof Error) {
          cancelarVoltar();
          setOpenConfirm(false);
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
          return;
        }
        concluirVoltar();
        setOpenConfirm(false);
        setTms('success');
        setMsg('Baixa do caixa desfeita com sucesso.');
        setOpenmsg(true);
        if(contaSelecionada != ''){
          setConta(descricaoContaSelecionada);
          setIsLoading(true);
          CaixaConstasService.getAll('', contaSelecionada, dataInicial, dataFinal)
            .then((result2) => {
              setIsLoading(false);
              if (result2 instanceof Error) {
                setTms('error');
                setMsg(result2.message);
                setOpenmsg(true);
              } else {
                if(result2.lan.length > 0){
                  setRows(result2.lan);
                  setStatus(result2.lan[0].status);
                  setPesquisaRealizada(true);
                }else{
                  setRows([]);
                  setStatus(false);
                  setPesquisaRealizada(true);
                }
                if (typeof result2.resultado === 'object')
                  setResultados(result2.resultado);
              }
            });
        }
      });
  };

  const handleCancelDelete = () => {
    if (isVoltando) return;
    setOpenConfirm(false);
  };

  // Busca compartilhada por todo mundo que precisa (re)carregar a listagem:
  // seleção de conta, botão pesquisar, troca de datas, e o retorno dos
  // diálogos de abrir/fechar. Recebe conta/datas por parâmetro (não lê do
  // estado) porque quem acabou de mudar a conta ou as datas ainda não vê
  // esse novo valor refletido no estado dentro da mesma função (setState é
  // assíncrono) — só assim dá pra buscar já com o valor novo, sem esperar
  // um re-render.
  const buscarLancamentos = (idconta: string, descricao: string, di: string, df: string) => {
    setIsLoading(true);
    setConta(descricao);
    CaixaConstasService.getAll('', idconta, di, df)
      .then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
          setPesquisaRealizada(true);
        } else {
          if (result.lan.length > 0) {
            setRows(result.lan);
            setStatus(result.lan[0].status);
          } else {
            setRows([]);
            setStatus(false);
          }
          setPesquisaRealizada(true);
          if (typeof result.resultado === 'object')
            setResultados(result.resultado);
        }
      })
      .catch(() => {
        setIsLoading(false);
        setTms('error');
        setMsg('Erro ao carregar os dados');
        setOpenmsg(true);
        setPesquisaRealizada(true);
      });
  };

  // Função para quando selecionar a conta — agora chamada pela sidebar (ver
  // SidebarContasConciliacao), que já sabe o tipo da conta escolhida. Já
  // busca os lançamentos do dia direto, sem precisar clicar em "Pesquisar".
  const handleSelecionarConta = (idconta: string, descricao: string, tipo: TTipoContaFinanceira) => {
    setContaSelecionada(idconta);
    setDescricaoContaSelecionada(descricao);
    setTipoContaSelecionada(tipo);
    // Limpa os resultados da conta anterior, pra não mostrar dado velho
    // enquanto a busca do dia (abaixo) ainda não voltou.
    setRows([]);
    setResultados(undefined);
    setStatus(false);
    setPesquisaRealizada(false);
    setAbaFiltro('todos');
    // Sempre parte do dia atual ao trocar de conta, mesmo que a pesquisa
    // anterior estivesse com outro período selecionado.
    const hoje = dayjs().format('DD/MM/YYYY');
    setDataInicial(hoje);
    setDataFinal(hoje);
    // Atualiza a URL sem recarregar a página
    setSearchParams({ conta: idconta, descricaoconta: descricao }, { replace: true });
    buscarLancamentos(idconta, descricao, hoje, hoje);

    setStatusSessaoCaixa(null);
    if (tipo === 'caixa') {
      CaixaTurnoAdminService.getStatus(idconta).then((resultado) => {
        if (!(resultado instanceof Error)) setStatusSessaoCaixa(resultado);
      });
    }
  };

  // Função para pesquisar (chamada pelo botão pesquisar)
  const handlePesquisar = () => {
    if(contaSelecionada === '') {
      setTms('warning');
      setMsg('Selecione uma conta antes de pesquisar');
      setOpenmsg(true);
      return;
    }
    buscarLancamentos(contaSelecionada, descricaoContaSelecionada, dataInicial, dataFinal);
  };

  const handleClose = (newValue?: string) => {
    setOpenAbrir(false);
    setOpenFechar(false);
    setOpenTransferirRecebivel(false);
    setOpenTransferenciaConta(false);
    if (newValue === 'true' && contaSelecionada != '') {
      buscarLancamentos(contaSelecionada, descricaoContaSelecionada, dataInicial, dataFinal);
    }
  };

  const handlePesquisa = (datainicial: Dayjs | null, datafinal: Dayjs | null) => {
    const formattedDateInicial = datainicial ? dayjs(datainicial).format('DD/MM/YYYY') : '';
    const formattedDateFinal = datafinal ? dayjs(datafinal).format('DD/MM/YYYY') : '';
    setDataInicial(formattedDateInicial);
    setDataFinal(formattedDateFinal);

    if(contaSelecionada === '') {
      setTms('warning');
      setMsg('Selecione uma conta antes de pesquisar');
      setOpenmsg(true);
      return;
    }
    buscarLancamentos(contaSelecionada, descricaoContaSelecionada, formattedDateInicial, formattedDateFinal);
  };

  const handlePrint = () => {
    setIsLoading(true);    
    
    if(contaSelecionada != ''){ 
      setConta(descricaoContaSelecionada);
      CaixaConstasService.getAll('impressao', contaSelecionada, dataInicial, dataFinal)
        .then((result) => {
          setIsLoading(false);  
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            if(result.lan.length > 0){
              if (typeof result.resultado === 'object')
              console.log(result.lan);
              PDFCaixa(result.lan, result.resultado, conta);
            } else {
              setTms('warning');
              setMsg('Não há dados para imprimir');
              setOpenmsg(true);
            }
          }
        });
    } else {
      setTms('warning');
      setMsg('Selecione uma conta para imprimir');
      setOpenmsg(true);
      setIsLoading(false);
    }
  };    

  const handleFechar = () => {
    if(contaSelecionada === '') {
      setTms('warning');
      setMsg('Selecione uma conta para fechar');
      setOpenmsg(true);
      return;
    }
    setOpenFechar(true);
  };

  const handleAbrir = () => {
    if(contaSelecionada === '') {
      setTms('warning');
      setMsg('Selecione uma conta para abrir');
      setOpenmsg(true);
      return;
    }
    setOpenAbrir(true);
  };

  const handleTransferirRecebivel = () => {
    if (contaSelecionada === '') {
      setTms('warning');
      setMsg('Selecione uma conta de recebíveis para transferir');
      setOpenmsg(true);
      return;
    }
    setOpenTransferirRecebivel(true);
  };

  const handleAbrirTransferenciaConta = (destino: TDestinoTransferenciaConta) => {
    if (contaSelecionada === '') {
      setTms('warning');
      setMsg('Selecione uma conta para transferir');
      setOpenmsg(true);
      return;
    }
    setDestinoTransferenciaConta(destino);
    setOpenTransferenciaConta(true);
  };

  const handleAbrirDetalhamento = () => {
    if (rows.length === 0) {
      setTms('warning');
      setMsg('Não há lançamentos para detalhar');
      setOpenmsg(true);
      return;
    }
    setOpenDetalhamento(true);
  };

  const buscarSaldosPorConta = (dataIni: Dayjs | null, dataFim: Dayjs | null) => {
    setCarregandoSaldosPorConta(true);
    const datainicial = (dataIni ?? dayjs()).format('YYYY-MM-DD');
    const datafinal = (dataFim ?? dayjs()).format('YYYY-MM-DD');
    CaixaConstasService.getSaldosPorConta(datainicial, datafinal).then((result) => {
      setCarregandoSaldosPorConta(false);
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
        return;
      }
      setSaldosPorConta(result.porConta);
      setRecebiveisPorFormaPagamento(result.recebiveisPorFormaPagamento);
      setTotaisRecebidos(result.totaisRecebidos);
    });
  };

  const handleAbrirSaldosPorConta = () => {
    setOpenSaldosPorConta(true);
    setPeriodoSaldosPreset('hoje');
    setDataInicialSaldos(dayjs());
    setDataFinalSaldos(dayjs());
    setGruposSaldosFechados(new Set());
    setTotaisRecebidos({ credito: 0, debito: 0, pixDinamico: 0, dinheiro: 0, contasBancarias: 0 });
    buscarSaldosPorConta(dayjs(), dayjs());
  };

  const aplicarPresetSaldos = (preset: TPeriodoSaldosPreset) => {
    setPeriodoSaldosPreset(preset);
    const hoje = dayjs();
    if (preset === 'hoje') {
      setDataInicialSaldos(hoje);
      setDataFinalSaldos(hoje);
    } else if (preset === 'ontem') {
      const ontem = hoje.subtract(1, 'day');
      setDataInicialSaldos(ontem);
      setDataFinalSaldos(ontem);
    } else if (preset === '7dias') {
      setDataInicialSaldos(hoje.subtract(6, 'day'));
      setDataFinalSaldos(hoje);
    } else if (preset === 'mes') {
      setDataInicialSaldos(hoje.startOf('month'));
      setDataFinalSaldos(hoje);
    }
    // 'personalizado' não mexe nas datas — o usuário edita direto os campos.
  };

  const toggleGrupoSaldos = (tipo: string) => {
    setGruposSaldosFechados((atual) => {
      const novo = new Set(atual);
      if (novo.has(tipo)) novo.delete(tipo);
      else novo.add(tipo);
      return novo;
    });
  };

  // Saldo acumulado por linha — calculado sobre TODOS os `rows` (ordem
  // original do backend, `order by datalan, numero`) antes de qualquer
  // filtro, senão a soma perde as entradas/saídas das linhas excluídas pela
  // aba selecionada e o acumulado fica errado a partir dali.
  const rowsComSaldo = useMemo(() => {
    let saldoCorrente = resultados?.saldoanterior ?? 0;
    return rows.map((row) => {
      // Lançamento revertido continua na lista como histórico, mas o valor
      // não conta mais no saldo (mesmo critério do backend, ver GetAll.ts).
      if (!row.revertido) {
        saldoCorrente += Number(row.entrada || 0) - Number(row.saida || 0);
      }
      return { ...row, saldoAcumulado: saldoCorrente };
    });
  }, [rows, resultados?.saldoanterior]);

  // Abas de filtro (Todos/Créditos/Débitos/Transferências/Sangrias/
  // Suprimentos) — puramente client-side sobre o que já foi buscado, sem
  // chamada nova à API. "Suprimentos" fica sempre vazia: não existe um tipo
  // de lançamento distinto de "Transferência" pra isso no sistema hoje.
  const rowsExibidas = useMemo(() => {
    if (abaFiltro === 'todos') return rowsComSaldo;
    if (abaFiltro === 'suprimentos') return [];
    return rowsComSaldo.filter((r) => classificarLinha(r) === abaFiltro);
  }, [rowsComSaldo, abaFiltro]);

  return (
    <LayoutBaseDeFinanceiroConciliacao
      saldoanterior={formatarMoeda(resultados?.saldoanterior ?? 0)}
      credito={formatarMoeda(resultados?.credito ?? 0)}
      debito={formatarMoeda(resultados?.debito ?? 0)}
      saldo={formatarMoeda(resultados?.saldo ?? 0)}
      saldoreal={formatarMoeda(resultados?.saldoreal ?? 0)}
      aoClicarEmSaldoReal={handleAbrirDetalhamento}
      exibirTotaisGerais={!pesquisaRealizada}
      totalRecebiveis={totalPorTipo('recebiveis')}
      totalCofre={totalPorTipo('cofre')}
      totalCaixa={totalPorTipo('caixa')}
      totalContasBancarias={totalPorTipo('banco')}
      sidebar={
        <SidebarContasConciliacao
          contaSelecionadaId={contaSelecionada}
          aoSelecionarConta={handleSelecionarConta}
        />
      }
      barraDeFerramentas={
        <FerramentasDaListagemFinanceiroConciliacao
          mostrarBotaoAbrirConta={status ? true : false}
          mostrarBotaoFecharConta={status ? false : true}
          statusSessaoCaixa={tipoContaSelecionada === 'caixa' && statusSessaoCaixa ? (statusSessaoCaixa.aberto ? 'aberta' : 'fechada') : undefined}
          mostrarBotaoTransferirRecebivel={tipoContaSelecionada === 'recebiveis'}
          mostrarBotaoTransferencia={tipoContaSelecionada === 'banco' || tipoContaSelecionada === 'cofre'}
          tipoContaOrigemTransferencia={tipoContaSelecionada === 'cofre' ? 'cofre' : 'banco'}
          aoClicarEmFecharConta={handleFechar}
          aoClicarEmImpressao={handlePrint}
          aoClicarEmAbrirConta={handleAbrir}
          aoClicarEmTransferirRecebivel={handleTransferirRecebivel}
          aoClicarEmTransferenciaParaConta={() => handleAbrirTransferenciaConta('banco')}
          aoClicarEmTransferenciaParaCaixa={() => handleAbrirTransferenciaConta('caixa')}
          aoClicarEmPesquisa={handlePesquisa}
          contaSelecionadaLabel={descricaoContaSelecionada}
          aoClicarEmDetalharSaldos={handleAbrirSaldosPorConta}
        />
      }
    >
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg}  autoHideDuration={900} onClose={handleCloseMsg}>
        <Alert
          onClose={handleCloseMsg}
          severity={tms}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {msg}
        </Alert>
      </Snackbar>
      <TelaBloqueioSalvando
        open={isVoltando}
        message="Desfazendo lançamento..."
        progress={voltarProgress}
      />
      <Dialog open={openConfirm} onClose={handleCancelDelete} maxWidth="sm" fullWidth>
        <DialogTitle>Voltar Lançamento</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {registrosParaVoltar.length > 1
              ? 'Estes lançamentos fazem parte da mesma operação e serão desfeitos juntos, mesmo em contas diferentes:'
              : 'Este lançamento será desfeito:'}
          </Typography>
          {carregandoRegistrosVoltar ? (
            <LinearProgress sx={{ my: 2 }} />
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Número</TableCell>
                    <TableCell>Conta</TableCell>
                    <TableCell>Histórico</TableCell>
                    <TableCell align='right'>Crédito</TableCell>
                    <TableCell align='right'>Débito</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {registrosParaVoltar.map((registro) => (
                    <TableRow key={registro.idcaixa}>
                      <TableCell>{formatarDataHora(registro.datalan, registro.criadoem)}</TableCell>
                      <TableCell>{registro.numero}</TableCell>
                      <TableCell>{registro.nomeConta}</TableCell>
                      <TableCell>{registro.historico}</TableCell>
                      <TableCell align='right'>{formatarMoeda(Number(registro.entrada || 0))}</TableCell>
                      <TableCell align='right'>{formatarMoeda(Number(registro.saida || 0))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 4, mt: 2 }}>
            {totalEntradaVoltar > 0 && (
              <Typography variant="subtitle2">
                Total Crédito: {formatarMoeda(totalEntradaVoltar)}
              </Typography>
            )}
            {totalSaidaVoltar > 0 && (
              <Typography variant="subtitle2">
                Total Débito: {formatarMoeda(totalSaidaVoltar)}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={handleCancelDelete} disabled={isVoltando}>Não</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={isVoltando || carregandoRegistrosVoltar || registrosParaVoltar.length === 0}>Sim, Voltar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDetalhamento} onClose={() => setOpenDetalhamento(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Detalhamento do Saldo Real</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {categoriasDetalhamento.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nenhum valor no caixa no período.
              </Typography>
            ) : categoriasDetalhamento.map((categoria) => (
              <Box key={categoria} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1">{categoria}</Typography>
                <Typography variant="subtitle1" fontWeight={600}>
                  {formatarMoeda(detalhamentoPorFormaPagamento.get(categoria) || 0)}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setOpenDetalhamento(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openSaldosPorConta} onClose={() => setOpenSaldosPorConta(false)} maxWidth="sm" fullWidth>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', px: 3, pt: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: theme.palette.info.main, color: '#fff',
            }}>
              <PaidIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>Detalhamento de Saldos por Conta</Typography>
              <Typography variant="body2" color="text.secondary">
                Visualize o saldo detalhado por tipo de conta e forma de pagamento.
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setOpenSaldosPorConta(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent dividers>
          <Box sx={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 2,
            p: 2, mb: 3, borderRadius: 2,
            bgcolor: alpha(theme.palette.info.main, 0.06),
            border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
          }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="periodo-saldos-label">Período</InputLabel>
              <Select
                labelId="periodo-saldos-label"
                label="Período"
                value={periodoSaldosPreset}
                onChange={(e) => aplicarPresetSaldos(e.target.value as TPeriodoSaldosPreset)}
              >
                <MenuItem value="hoje">Hoje</MenuItem>
                <MenuItem value="ontem">Ontem</MenuItem>
                <MenuItem value="7dias">Últimos 7 dias</MenuItem>
                <MenuItem value="mes">Este mês</MenuItem>
                <MenuItem value="personalizado">Personalizado</MenuItem>
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Data inicial"
                format="DD/MM/YYYY"
                value={dataInicialSaldos}
                onChange={(newValue) => { setDataInicialSaldos(newValue); setPeriodoSaldosPreset('personalizado'); }}
                slotProps={{ textField: { size: 'small' } }}
                sx={{ width: 160 }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Data final"
                format="DD/MM/YYYY"
                value={dataFinalSaldos}
                onChange={(newValue) => { setDataFinalSaldos(newValue); setPeriodoSaldosPreset('personalizado'); }}
                slotProps={{ textField: { size: 'small' } }}
                sx={{ width: 160 }}
              />
            </LocalizationProvider>
            <Button
              variant="contained"
              color="info"
              startIcon={<SearchIcon />}
              onClick={() => buscarSaldosPorConta(dataInicialSaldos, dataFinalSaldos)}
              disabled={carregandoSaldosPorConta}
            >
              Filtrar
            </Button>
          </Box>

          {carregandoSaldosPorConta ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
            <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={700}>Totais Recebidos no Período</Typography>
              <Typography variant="caption" color="text.secondary">
                Valor bruto recebido em cada forma (soma de entradas, sem descontar transferências/saídas).
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1.5 }}>
                {itensTotaisRecebidos.map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      flex: '1 1 160px', minWidth: 160,
                      display: 'flex', alignItems: 'center', gap: 1.25,
                      p: 1.5, borderRadius: 2,
                      border: `1px solid ${alpha(item.cor, 0.25)}`,
                      bgcolor: alpha(item.cor, 0.06),
                    }}
                  >
                    <Box sx={{
                      width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: alpha(item.cor, 0.16), color: item.cor,
                    }}>
                      {item.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" display="block" noWrap>
                        {item.label}
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={700} color={item.valor < 0 ? 'error.main' : 'text.primary'}>
                        {formatarMoeda(item.valor)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {saldosAgrupados.map((grupo) => {
                const fechado = gruposSaldosFechados.has(grupo.tipo);
                return (
                  <Paper key={grupo.tipo} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <Box
                      onClick={() => toggleGrupoSaldos(grupo.tipo)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, cursor: 'pointer' }}
                    >
                      <Box sx={{
                        width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: alpha(theme.palette.info.main, 0.12), color: theme.palette.info.main,
                      }}>
                        {grupo.icon}
                      </Box>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700}>{grupo.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{grupo.descricao}</Typography>
                      </Box>
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        color={grupo.total < 0 ? 'error.main' : 'info.main'}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {formatarMoeda(grupo.total)}
                      </Typography>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleGrupoSaldos(grupo.tipo); }}>
                        {fechado ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                      </IconButton>
                    </Box>
                    <Collapse in={!fechado}>
                      <Divider />
                      <Box sx={{ px: 2, py: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {grupo.itens.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                            Nenhum valor encontrado
                          </Typography>
                        ) : grupo.itens.map((item) => (
                          <Box key={item.chave} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: theme.palette.info.main, flexShrink: 0 }} />
                            <Typography variant="body2" sx={{ flexGrow: 1 }}>{item.label}</Typography>
                            <Typography variant="body2" fontWeight={600} color={item.valor < 0 ? 'error.main' : 'text.primary'}>
                              {formatarMoeda(item.valor)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  </Paper>
                );
              })}
            </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, borderRadius: 2,
            bgcolor: alpha(theme.palette.info.main, 0.06),
          }}>
            <CalculateOutlinedIcon color="info" />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">Saldo Total Geral</Typography>
              <Typography variant="subtitle1" fontWeight={700} color={saldoTotalGeral < 0 ? 'error.main' : 'info.main'}>
                {formatarMoeda(saldoTotalGeral)}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, minWidth: 200 }}>
            <InfoOutlinedIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              Os valores apresentados consideram todas as movimentações no período selecionado.
            </Typography>
          </Box>
          <Button color="inherit" variant="outlined" onClick={() => setOpenSaldosPorConta(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!linhaDetalhe} onClose={() => setLinhaDetalhe(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Detalhes do Lançamento</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Typography variant="body2"><b>Data:</b> {linhaDetalhe && formatarDataHora(linhaDetalhe.datalan, linhaDetalhe.criadoem)}</Typography>
              <Typography variant="body2"><b>Número:</b> {linhaDetalhe?.numero || '—'}</Typography>
              <Typography variant="body2"><b>Sessão:</b> {linhaDetalhe?.numerosessao || '—'}</Typography>
              <Typography variant="body2"><b>Parceiro:</b> {linhaDetalhe?.nome || '—'}</Typography>
              {linhaDetalhe?.revertido && (
                <Typography variant="body2" color="text.secondary">
                  <b>Revertido em:</b> {dayjs(linhaDetalhe.revertidoem).format('DD/MM/YYYY [às] HH:mm')}
                </Typography>
              )}
            </Box>
            <TextField
              label="Histórico completo"
              value={linhaDetalhe?.historico || ''}
              multiline
              minRows={4}
              fullWidth
              InputProps={{ readOnly: true }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
              <Typography variant="body2" color="success.main"><b>Crédito:</b> {formatarMoeda(Number(linhaDetalhe?.entrada || 0))}</Typography>
              <Typography variant="body2" color="error.main"><b>Débito:</b> {formatarMoeda(Number(linhaDetalhe?.saida || 0))}</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setLinhaDetalhe(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <>
          {/* Loading Indicator */}
          {isLoading && (
            <LinearProgress sx={{ mx: 1, my: 0 }} />
          )}

          {/* Abas de filtro — client-side sobre os dados já buscados */}
          <Box sx={{ mx: 1, mb: 1 }}>
            <FilterBar
              chips={ABAS_FILTRO.map((aba) => ({ value: aba.valor, label: aba.label }))}
              value={abaFiltro}
              onChange={(novaAba) => setAbaFiltro(novaAba)}
            />
          </Box>

          {/* Table Container with Scroll only inside the table */}
          <Box sx={{ height: 'calc(100vh - 360px)', display: 'flex', flexDirection: 'column', mx: 1 }}>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                flex: 1,
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: (theme) => theme.palette.grey[200],
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: (theme) => theme.palette.grey[400],
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.grey[500],
                  },
                },
              }}
            >
              <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: (theme) => theme.palette.background.paper, py: 1, fontWeight: 700 }}>Tipo</TableCell>
                      <TableCell sx={{ backgroundColor: (theme) => theme.palette.background.paper, py: 1, fontWeight: 700 }}>Data</TableCell>
                      <TableCell align='center' sx={{ backgroundColor: (theme) => theme.palette.background.paper, py: 1, fontWeight: 700 }}>Numero</TableCell>
                      <TableCell align='center' sx={{ backgroundColor: (theme) => theme.palette.background.paper, py: 1, fontWeight: 700 }}>Sessão</TableCell>
                      <TableCell align='left' sx={{ backgroundColor: (theme) => theme.palette.background.paper, py: 1, fontWeight: 700 }}>Parceiro</TableCell>
                      <TableCell align='left' sx={{ backgroundColor: (theme) => theme.palette.background.paper, py: 1, fontWeight: 700 }}>Histórico</TableCell>
                      <TableCell align='right' sx={{ backgroundColor: (theme) => theme.palette.background.paper, py: 1, fontWeight: 700 }}>Crédito</TableCell>
                      <TableCell align='right' sx={{ backgroundColor: (theme) => theme.palette.background.paper, py: 1, fontWeight: 700 }}>Débito</TableCell>
                      <TableCell align='right' sx={{ backgroundColor: (theme) => theme.palette.background.paper, py: 1, fontWeight: 700 }}>Saldo</TableCell>
                      <TableCell sx={{ maxWidth: 15, width:15, backgroundColor: (theme) => theme.palette.background.paper, py: 1, fontWeight: 700 }} align='center'>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading && rows.length === 0 ? (
                      <LinhasEsqueletoTabela
                        colunas={[
                          { variant: 'circular', width: 20, height: 20 },
                          { width: '65%' },
                          { width: '50%', align: 'center' },
                          { width: '55%', align: 'center' },
                          { width: '75%' },
                          { width: '85%' },
                          { width: '55%', align: 'right' },
                          { width: '55%', align: 'right' },
                          { width: '55%', align: 'right' },
                          { variant: 'circular', width: 24, height: 24, align: 'center' },
                        ]}
                      />
                    ) : !pesquisaRealizada ? (
                      <TableRow>
                        <TableCell colSpan={10} sx={{ py: 0 }}>
                          <EmptyState
                            icon={<AccountBalanceIcon />}
                            title={!contaSelecionada ? 'Selecione uma conta' : 'Clique em pesquisar'}
                            description={
                              !contaSelecionada
                                ? 'Escolha uma conta na barra lateral e clique em pesquisar para visualizar os lançamentos'
                                : 'Selecione uma conta e clique no botão de pesquisa para visualizar os lançamentos'
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ) : rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} sx={{ py: 0 }}>
                          <EmptyState
                            icon={<SearchOffIcon />}
                            title="Nenhum lançamento encontrado"
                            description={`Não há registros para a conta ${conta} no período de ${dataInicial} a ${dataFinal}`}
                            actionLabel="Pesquisar novamente"
                            onAction={() => handlePesquisa(dayjs(dataInicial, 'DD/MM/YYYY'), dayjs(dataFinal, 'DD/MM/YYYY'))}
                          />
                        </TableCell>
                      </TableRow>
                    ) : rowsExibidas.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} sx={{ py: 0 }}>
                          <EmptyState
                            icon={<SearchOffIcon />}
                            title="Nenhum lançamento nessa categoria"
                            description="Não há lançamentos dessa categoria no período selecionado."
                          />
                        </TableCell>
                      </TableRow>
                    ) : (
                      rowsExibidas.map(row => {
                        const categoriaLinha = classificarLinha(row);
                        const badge = BADGE_POR_CATEGORIA[categoriaLinha];
                        return (
                          <TableRow key={row.idcaixa}
                            hover
                            sx={{
                              '&:nth-of-type(odd)': { backgroundColor: (theme) => theme.palette.action.hover },
                            }}
                          >
                            <TableCell sx={{ py: 0.5, color: badge.cor }}>
                              {badge.icon}
                            </TableCell>
                            <TableCell align='left' sx={{ py: 0.5, fontSize: '0.875rem' }}>{formatarDataHora(row.datalan, row.criadoem)}</TableCell>
                            <TableCell align='center' sx={{ py: 0.5, fontSize: '0.875rem' }}>{row.numero}</TableCell>
                            <TableCell align='center' sx={{ py: 0.5, fontSize: '0.8125rem', fontFamily: 'monospace', fontWeight: 600 }}>{row.numerosessao || '—'}</TableCell>
                            <TableCell align='left' sx={{ py: 0.5, fontSize: '0.875rem' }}>{row.nome}</TableCell>
                            <TableCell
                              align='left'
                              sx={{ py: 0.5, fontSize: '0.875rem', maxWidth: 220, cursor: 'pointer' }}
                              onClick={() => setLinhaDetalhe(row)}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
                                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {row.historico}
                                </Box>
                                {row.revertido && (
                                  <Tooltip title={formatarDataHoraRevertido(row.revertidoem)} arrow>
                                    <Chip
                                      label="Revertido"
                                      size="small"
                                      color="default"
                                      sx={{ height: '16px', fontSize: '0.55rem', flexShrink: 0 }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align='right' sx={{ py: 0.5, fontSize: '0.875rem', color: 'success.main', textDecoration: row.revertido ? 'line-through' : 'none', opacity: row.revertido ? 0.5 : 1 }}>{formatarMoeda(row.entrada)}</TableCell>
                            <TableCell align='right' sx={{ py: 0.5, fontSize: '0.875rem', color: 'error.main', textDecoration: row.revertido ? 'line-through' : 'none', opacity: row.revertido ? 0.5 : 1 }}>{formatarMoeda(row.saida)}</TableCell>
                            <TableCell align='right' sx={{ py: 0.5, fontSize: '0.875rem' }}>{formatarMoeda(row.saldoAcumulado)}</TableCell>
                            <TableCell sx={{ py: 0.5 }} align='center'>
                              {row.revertido || ehTurnoAberturaOuFechamento(row.historico) ? null : row.status === true ? (
                                <Switch size='small' checked disabled />
                              ) : (
                                <IconButton
                                  size="small"
                                  aria-label="ações do lançamento"
                                  onClick={(e) => setMenuLinha({ idcaixa: row.idcaixa, anchorEl: e.currentTarget })}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
              </Table>
            </TableContainer>
            {rowsExibidas.length > 0 && (
              <Box sx={{ mt: 1, px: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {rowsExibidas.length} registro{rowsExibidas.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            )}
          </Box>

          <Menu
            anchorEl={menuLinha?.anchorEl}
            open={!!menuLinha}
            onClose={() => setMenuLinha(null)}
          >
            <MenuItem
              onClick={() => {
                const row = rows.find((r) => r.idcaixa === menuLinha?.idcaixa);
                setMenuLinha(null);
                if (row) handleVoltar(row.idoperacao, row.datalan, row.idconta, row.tipo);
              }}
            >
              <ReplyIcon fontSize="small" sx={{ mr: 1 }} color="error" />
              Voltar lançamento
            </MenuItem>
          </Menu>
        </>


      <DialoFecharCaixa
        open={openFechar} 
        onClose={handleClose}
        idconta={contaSelecionada}
      /> 
      <DialogAbrirCaixa
        open={openAbrir}
        onClose={handleClose}
        idconta={contaSelecionada}
      />
      <DialogTransferirRecebivel
        open={openTransferirRecebivel}
        onClose={handleClose}
        idcontaRecebivel={contaSelecionada}
        nomeContaRecebivel={descricaoContaSelecionada}
      />
      <DialogTransferenciaConta
        open={openTransferenciaConta}
        destino={destinoTransferenciaConta}
        tipoContaOrigem={tipoContaSelecionada === 'cofre' ? 'cofre' : 'banco'}
        onClose={handleClose}
        idcontaOrigem={contaSelecionada}
        nomeContaOrigem={descricaoContaSelecionada}
      />
    </LayoutBaseDeFinanceiroConciliacao>
  );
};